import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Building2, 
  User, 
  Code2, 
  Users, 
  FileText,
  Calendar as CalendarIcon,
  Type,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { Footer } from '../components/Footer';
import { CompactHeader } from '../components/CompactHeader';


export default function AdvanceCauseListPage() {
  const [textSize, setTextSize] = useState('normal');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedFilter, setSelectedFilter] = useState(null);
  const navigate = useNavigate();

  const filterOptions= [
    {
      id: 'Judge',
      title: 'Judge',
      caption: 'Search by Judge',
      icon: <User className="w-10 h-10 text-blue-600" />,
      ariaLabel: 'Search by Judge'
    },
    {
      id: 'Court-Room',
      title: 'Court Room',
      caption: 'Search by Court Room',
      icon: <Building2 className="w-10 h-10 text-blue-600" />,
      ariaLabel: 'Search by Court Room'
    },
    {
      id: 'Party-Name',
      title: 'Party Name',
      caption: 'Search by Party Name',
      icon: <User className="w-10 h-10 text-blue-600" />,
      ariaLabel: 'Search by Party Name'
    },
    {
      id: 'Status',
      title: 'Status',
      caption: 'Search by Status',
      icon: <CheckCircle className="w-10 h-10 text-blue-600" />,
      ariaLabel: 'Search by Status'
    }
    // {
    //   id: 'case-wise',
    //   title: 'Case Wise',
    //   caption: 'Filter by case number',
    //   icon: <FileText className="w-10 h-10" />,
    //   ariaLabel: 'Filter cause list by case number'
    // }
  ];

  const adjustTextSize = (size) => {
    setTextSize(size);
  };

  const getFontSizeClass = () => {
    if (textSize === 'large') return 'text-lg';
    if (textSize === 'small') return 'text-sm';
    return 'text-base';
  };

  const handleViewResults = () => {
    if (selectedDate && selectedFilter) {
      navigate(`/cause-list/search?type=advance&filter=${selectedFilter}&date=${format(selectedDate, 'yyyy-MM-dd')}`);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <CompactHeader  />
      {/* Accessibility Controls Bar */}
      <div className="bg-[#1E293B] text-white py-3 px-4 sticky top-[100px] z-40">
        <div className="max-w-7xl mx-auto flex justify-end items-center">
          {/* Text Size Controls */}
          <div className="flex items-center gap-3">
            <Type className="w-5 h-5" aria-hidden="true" />
            <div className="flex gap-2" role="group" aria-label="Text size controls">
              <button
                onClick={() => adjustTextSize('small')}
                className={`px-3 py-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 ${
                  textSize === 'small' ? 'bg-white text-[#1E293B]' : 'bg-white/10 hover:bg-white/20'
                }`}
                aria-label="Small text size"
                aria-pressed={textSize === 'small'}
              >
                A-
              </button>
              <button
                onClick={() => adjustTextSize('normal')}
                className={`px-3 py-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 ${
                  textSize === 'normal' ? 'bg-white text-[#1E293B]' : 'bg-white/10 hover:bg-white/20'
                }`}
                aria-label="Normal text size"
                aria-pressed={textSize === 'normal'}
              >
                A
              </button>
              <button
                onClick={() => adjustTextSize('large')}
                className={`px-3 py-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 ${
                  textSize === 'large' ? 'bg-white text-[#1E293B]' : 'bg-white/10 hover:bg-white/20'
                }`}
                aria-label="Large text size"
                aria-pressed={textSize === 'large'}
              >
                A+
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Back Button Section */}
      <div className="bg-gray-50 dark:bg-slate-800 py-4 px-4 border-b border-gray-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/CauseLists"
            className="inline-flex items-center gap-2 text-[#155dfc] hover:text-[#6B1515] transition-colors focus:outline-none focus:ring-2 focus:ring-[#155dfc]/50 rounded-lg px-3 py-2"
            aria-label="Go back to Cause List home"
          >
            <ArrowLeft className="w-5 h-5" aria-hidden="true" />
            <span className={getFontSizeClass()}>
              Back to Cause List Home
            </span>
          </Link>
        </div>
      </div>

      {/* Header Section with Gradient Background */}
      <section className="relative py-24 bg-hero-light-image dark:bg-hero-dark">
        {/* Subtle overlay for better text readability */}
        <div className="absolute inset-0 bg-black/10"></div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className={`mb-4 text-3xl lg:text-4xl text-gray-900 drop-shadow-sm`}>
            Advance Cause List
          </h1>
          <p className={`${getFontSizeClass()} text-gray-800 drop-shadow-sm`}>
            Select a date and filter to view advance cause lists
          </p>
        </div>
      </section>

      {/* Date Selection Section */}
      <section className="py-12 px-4 bg-white dark:bg-slate-900">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-50 dark:bg-slate-800 rounded-2xl p-8 border-2 border-[#155dfc]/20">
            <h2 className={`${getFontSizeClass()} text-gray-900 dark:text-white mb-6 text-center`}>
              Step 1: Select Cause List Date
            </h2>
            
            <div className="flex justify-center">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full max-w-md justify-start text-left h-14 px-6 border-2 border-[#155dfc]/30 hover:border-[#155dfc] focus:ring-2 focus:ring-[#155dfc]/50"
                    aria-label="Select date for cause list"
                  >
                    <CalendarIcon className="mr-3 h-5 w-5 text-[#155dfc]" aria-hidden="true" />
                    <span className={getFontSizeClass()}>
                      {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Options Section */}
      <section className="py-12 px-4 bg-gray-50 dark:bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <h2 className={`${getFontSizeClass()} text-gray-900 dark:text-white mb-8 text-center`}>
            Step 2: Choose Filter Option
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {filterOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => setSelectedFilter(option.id)}
                className={`group bg-white dark:bg-slate-700 rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 p-8 flex flex-col items-center text-center min-h-[240px] focus:outline-none focus:ring-4 focus:ring-[#155dfc]/30 ${
                  selectedFilter === option.id 
                    ? 'border-4 border-[#155dfc] scale-105' 
                    : 'border-2 border-transparent hover:border-[#155dfc]'
                }`}
                aria-label={option.ariaLabel}
                aria-pressed={selectedFilter === option.id}
              >
                <div className={`mb-6 transition-all duration-300 ${
                  selectedFilter === option.id 
                    ? 'text-[#155dfc] scale-110' 
                    : 'text-[#155dfc]/70 group-hover:text-[#155dfc] group-hover:scale-110'
                }`}>
                  {option.icon}
                </div>
                <h3 className={`${getFontSizeClass()} text-gray-900 dark:text-white mb-3`}>
                  {option.title}
                </h3>
                <p className={`${getFontSizeClass()} text-gray-600 dark:text-gray-300`}>
                  {option.caption}
                </p>
                {selectedFilter === option.id && (
                  <div className="mt-4 px-4 py-2 text-blue-600 rounded-lg">
                    Selected
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* View Results Button */}
          {selectedDate && selectedFilter && (
            <div className="mt-12 text-center">
              <Button
                size="lg"
                onClick={handleViewResults}
                className="text-white px-12 py-6 h-auto focus:ring-4 "
                aria-label="View filtered advance cause list"
              >
                <span className={getFontSizeClass()}>
                  View Advance List
                </span>
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Help Section */}
      <section className="py-12 px-4 bg-white dark:bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-8 border-2 border-blue-200 dark:border-blue-800">
            <h3 className={`${getFontSizeClass()} text-gray-900 dark:text-white mb-4`}>
              Need Help?
            </h3>
            <p className={`${getFontSizeClass()} text-gray-700 dark:text-gray-300 mb-6`}>
              If you need assistance accessing the cause list or have any questions, please contact the court registry.
            </p>
            <Button
              variant="outline"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white focus:ring-2 focus:ring-blue-600/50"
            >
              Contact Support
            </Button>
          </div>
        </div>
      </section>
       <Footer />
    </div>
  );
}
